import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ItemMaster() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newAltSection, setNewAltSection] = useState('');
  const [formData, setFormData] = useState({
    part_id: '',
    part_name: '',
    steel_grade: '',
    section: '',
    alternate_sections: [],
    gross_weight: '',
    std_process_flow: ''
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/masters/items`, {
        
      });
      setItems(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        gross_weight: parseFloat(formData.gross_weight),
        alternate_sections: formData.alternate_sections,
        created_at: new Date().toISOString()
      };

      if (editingItem) {
        await axios.put(`${API}/masters/items/${editingItem.part_id}`, data, {
          
        });
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API}/masters/items`, data, {
          
        });
        toast.success('Item created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      part_id: item.part_id,
      part_name: item.part_name,
      steel_grade: item.steel_grade,
      section: item.section,
      alternate_sections: item.alternate_sections || [],
      gross_weight: item.gross_weight.toString(),
      std_process_flow: item.std_process_flow
    });
    setNewAltSection('');
    setDialogOpen(true);
  };

  const handleDelete = async (partId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${API}/masters/items/${partId}`, {
        
      });
      toast.success('Item deleted successfully');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      part_id: '',
      part_name: '',
      steel_grade: '',
      section: '',
      alternate_sections: [],
      gross_weight: '',
      std_process_flow: ''
    });
    setEditingItem(null);
    setNewAltSection('');
  };

  const addAltSection = () => {
    if (!newAltSection.trim()) return;
    if (formData.alternate_sections.includes(newAltSection.trim())) {
      toast.error('Section already added');
      return;
    }
    setFormData({...formData, alternate_sections: [...formData.alternate_sections, newAltSection.trim()]});
    setNewAltSection('');
  };

  const removeAltSection = (idx) => {
    setFormData({...formData, alternate_sections: formData.alternate_sections.filter((_, i) => i !== idx)});
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="item-master-title">ITEM MASTER</h1>
          <p className="text-sm text-gray-700 mt-2">Manage part specifications and details</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-item-button"
              onClick={openCreateDialog}
              className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              ADD ITEM
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                {editingItem ? 'EDIT ITEM' : 'NEW ITEM'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part ID</Label>
                  <Input
                    data-testid="part-id-input"
                    value={formData.part_id}
                    onChange={(e) => setFormData({...formData, part_id: e.target.value})}
                    disabled={!!editingItem}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part Name</Label>
                  <Input
                    data-testid="part-name-input"
                    value={formData.part_name}
                    onChange={(e) => setFormData({...formData, part_name: e.target.value})}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Steel Grade</Label>
                  <Input
                    data-testid="steel-grade-input"
                    value={formData.steel_grade}
                    onChange={(e) => setFormData({...formData, steel_grade: e.target.value})}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Section</Label>
                  <Input
                    data-testid="section-input"
                    value={formData.section}
                    onChange={(e) => setFormData({...formData, section: e.target.value})}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Gross Weight</Label>
                  <Input
                    data-testid="gross-weight-input"
                    type="number"
                    step="0.01"
                    value={formData.gross_weight}
                    onChange={(e) => setFormData({...formData, gross_weight: e.target.value})}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Std Process Flow</Label>
                  <Input
                    data-testid="process-flow-input"
                    value={formData.std_process_flow}
                    onChange={(e) => setFormData({...formData, std_process_flow: e.target.value})}
                    required
                    className="mt-1.5 rounded-none"
                  />
                </div>
              </div>
              {/* Alternate Sections */}
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Alternate Sections</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    data-testid="alt-section-input"
                    value={newAltSection}
                    onChange={(e) => setNewAltSection(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAltSection(); }}}
                    placeholder="Add alternate section..."
                    className="rounded-none flex-1"
                  />
                  <Button type="button" onClick={addAltSection} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm px-3" data-testid="add-alt-section-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.alternate_sections.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.alternate_sections.map((sec, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-gray-100 border px-2.5 py-1 text-xs font-medium" data-testid={`alt-section-tag-${idx}`}>
                        {sec}
                        <button type="button" onClick={() => removeAltSection(idx)} className="hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 col-span-2">
                <Button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm"
                >
                  CANCEL
                </Button>
                <Button
                  data-testid="submit-item-button"
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                >
                  {editingItem ? 'UPDATE' : 'CREATE'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-gray-700">Loading items...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No items found. Create your first item.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Steel Grade</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Alt. Sections</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Gross Weight</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Process Flow</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.part_id} className="border-b transition-colors hover:bg-muted/50" data-testid={`item-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{item.part_id}</td>
                    <td className="p-4 align-middle">{item.part_name}</td>
                    <td className="p-4 align-middle">{item.steel_grade}</td>
                    <td className="p-4 align-middle">{item.section}</td>
                    <td className="p-4 align-middle">
                      <div className="flex flex-wrap gap-1">
                        {(item.alternate_sections || []).map((s, i) => (
                          <span key={i} className="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs text-blue-700">{s}</span>
                        ))}
                        {(!item.alternate_sections || item.alternate_sections.length === 0) && <span className="text-xs text-text-muted">—</span>}
                      </div>
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{item.gross_weight}</td>
                    <td className="p-4 align-middle">{item.std_process_flow}</td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`edit-item-${idx}`}
                          onClick={() => handleEdit(item)}
                          className="p-2 hover:bg-accent/10 hover:text-accent transition-all rounded-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-item-${idx}`}
                          onClick={() => handleDelete(item.part_id)}
                          className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}